import { formatDate } from '@angular/common';
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInYears } from 'date-fns';
import { Conversion, ConversionShared } from 'src/app/models/conversion.model';
import { CONVERSION_TIMEOUT_MS } from '../../constants';

export type ConversionRowState = 'processing' | 'success' | 'warn' | 'error' | 'timeout' | 'deleted';

export interface ConversionRowMetaPart {
  label: string;
  /** Hover explanation ("Uploaded <full date>", "Page count", "Parser") */
  tooltip?: string;
}

export interface ConversionRowView {
  state: ConversionRowState;
  /** Warn/error already corrected or refunded — the status glyph goes quiet grey */
  discMuted: boolean;
  fileName: string;
  /** Classic under-disc badge: "Corrected" / "Refunded" / "Cor & Ref" */
  discTag: string | null;
  /** "date · pages · parser [· user]" segments, each with a hover explanation */
  metaParts: ConversionRowMetaPart[];
  /** "3 issues" / "3 issues — fixed" link text; empty when there is nothing to show */
  issueLabel: string;
  originalUrl: string | null;
  csvUrl: string | null;
  xlsxUrl: string | null;
  isSensitive: boolean;
  /** ms until a processing row times out, so the host can re-evaluate; null otherwise */
  msUntilTimeout: number | null;
}

/** Firestore Timestamp (user app), ISO string (Algolia/admin app), or Date → Date */
export function toJsDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate();
  return null;
}

function fmt(date: Date | null): string {
  return date ? formatDate(date, 'medium', 'en-US') : '';
}

/** Succinct relative label: "just now", "5m ago", "3h ago", "2d ago", "4mo ago", "1y ago" */
export function shortRelativeDate(date: Date): string {
  const now = Date.now();
  const mins = differenceInMinutes(now, date);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = differenceInHours(now, date);
  if (hours < 24) return `${hours}h ago`;
  const days = differenceInDays(now, date);
  if (days < 31) return `${days}d ago`;
  const months = differenceInMonths(now, date);
  if (months < 12) return `${months}mo ago`;
  return `${differenceInYears(now, date)}y ago`;
}

function deriveState(c: ConversionShared, uploadDate: Date | null): ConversionRowState {
  if (c.deleted) return 'deleted';

  if (!c.healthCheck) {
    const withinTimeout = uploadDate && uploadDate.getTime() > Date.now() - CONVERSION_TIMEOUT_MS;
    return withinTimeout ? 'processing' : 'timeout';
  }

  if (c.healthCheck.healthy) return 'success';

  // Full Conversion docs carry the errors array; Algolia records only errorCount.
  const errors = (c as Conversion).healthCheck?.errors;
  if (errors) return errors.some((e) => e.type === 'Exception') ? 'error' : 'warn';
  return c.healthCheck.errorCount === 1 ? 'error' : 'warn';
}

export function buildConversionRowView(c: ConversionShared, opts: { isAdmin?: boolean } = {}): ConversionRowView {
  const uploadDate = toJsDate(c.uploadDate);
  const state = deriveState(c, uploadDate);

  // Exactly the classic labels, combined the classic way.
  const discTag = c.corrected && c.refunded ? 'Cor & Ref' : c.corrected ? 'Corrected' : c.refunded ? 'Refunded' : null;

  const metaParts: ConversionRowMetaPart[] = [];
  if (state === 'processing') metaParts.push({ label: 'Converting' });
  if (state !== 'processing' && uploadDate) metaParts.push({ label: shortRelativeDate(uploadDate), tooltip: `Uploaded ${fmt(uploadDate)}` });
  if (state === 'deleted') {
    const deletedDate = toJsDate(c.deletedDate);
    if (deletedDate) metaParts.push({ label: `Deleted ${shortRelativeDate(deletedDate)}`, tooltip: `Deleted ${fmt(deletedDate)}` });
  } else {
    if (c.pageCount) metaParts.push({ label: `${c.pageCount} ${c.pageCount === 1 ? 'page' : 'pages'}`, tooltip: 'Page count' });
    if (c.parserDisplay) metaParts.push({ label: c.parserDisplay, tooltip: 'Parser' });
  }
  if (opts.isAdmin && c.userName) metaParts.push({ label: `${c.userName} (${c.countryCode || 'No location'})`, tooltip: 'User' });

  const issueCount = c.healthCheck?.errorCount ?? (c as Conversion).healthCheck?.errors?.length ?? 0;
  const showIssues = (state === 'warn' || state === 'error') && issueCount > 0;
  const issueLabel = showIssues ? `${issueCount} ${issueCount === 1 ? 'issue' : 'issues'}${c.corrected ? ' — fixed' : ''}` : '';

  const showDownloads = state !== 'deleted' && state !== 'processing';

  return {
    state,
    discMuted: (state === 'warn' || state === 'error') && (!!c.corrected || !!c.refunded),
    fileName: c.fileName,
    discTag,
    metaParts,
    issueLabel,
    originalUrl: showDownloads ? c.downloadUrl || null : null,
    csvUrl: showDownloads ? c.csvDownloadLink || null : null,
    xlsxUrl: showDownloads ? c.xlsxDownloadLink || null : null,
    isSensitive: !!c.isSensitive,
    msUntilTimeout: state === 'processing' && uploadDate ? Math.max(0, uploadDate.getTime() + CONVERSION_TIMEOUT_MS - Date.now()) : null,
  };
}
